import { makeStyles } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import Page from '../../@sprinx/react-web/Page';
import { productDetailQuery } from '../../api/products/productDetail';
import { isProductCanceled } from '../../helpers/isProductCanceled';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';
import ProductDetailActions from './components/ProductDetailActions';
import ProductDetailDescription from './components/ProductDetailDescription';
import ProductDetailDiscounts from './components/ProductDetailDiscounts';
import ProductDetailImageGallery from './components/ProductDetailImageGallery';
import ProductDetailName from './components/ProductDetailName';
import ProductDetailOtherPrices from './components/ProductDetailOtherPrices';
import ProductDetailPageBreadcrumbs from './components/ProductDetailPageBreadcrumbs';
import ProductDetailSpagetty from './components/ProductDetailSpagetty';
import ProductDetailVariants from './components/ProductDetailVariants';
import ProductDetailAlternatives from './components/ProductDetailAlternatives';

export type ProductDetailPageLayoutProps = StandardDivProps<ProductDetailPageLayoutClassKey>;

export type ProductDetailPageLayoutClassKey = 'root' | 'contentWrapper' | 'image' | 'name';

const themeSettings = { name: 'ProductDetailPageLayout' };
const useStyles = makeStyles(
  (theme) => ({
    root: {},

    contentWrapper: {
      width: '100%',
      paddingTop: theme.spacing(2.5),
      display: 'grid',
      gridColumnGap: 0,
      gridRowGap: 0,
      justifyItems: 'stretch',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      gridTemplateColumns: 'minmax(306px, 40%) auto auto 1fr',
      gridTemplateAreas: `
        "imageGallery     name            name            name"
        "imageGallery     description     description     description"
        "imageGallery     discounts       discounts       discounts"
        "imageGallery     actions         actions         actions"
        "otherPrices      otherPrices     otherPrices     otherPrices"
        "variants         variants        variants        variants"
        "spagetty         spagetty        spagetty        spagetty"
        `,
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `
        "name"
        "imageGallery"
        "description"
        "discounts"
        "actions"
        "otherPrices"
        "variants"
        "spagetty"
        `,
      },

      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-columns': '40% 60%',
      },
    },
    imageGallery: {
      gridArea: 'imageGallery',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1 / 5,
        '-ms-grid-column': 1,
      },
      [theme.breakpoints.down('sm')]: {
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          '-ms-grid-row': 2,
          '-ms-grid-column': 1,
        },
      },
    },
    name: {
      gridArea: 'name',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 2 / 4,
      },
      [theme.breakpoints.down('sm')]: {
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          '-ms-grid-row': 1,
          '-ms-grid-column': 1,
        },
      },
    },
    description: {
      gridArea: 'description',

      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 2 / 4,
      },
      [theme.breakpoints.down('sm')]: {
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          '-ms-grid-row': 3,
          '-ms-grid-column': 1,
        },
      },
    },
    discounts: {
      gridArea: 'discounts',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 3,
        '-ms-grid-column': 2 / 4,
      },
      [theme.breakpoints.down('sm')]: {
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          '-ms-grid-row': 4,
          '-ms-grid-column': 1,
        },
      },
    },
    actions: {
      gridArea: 'actions',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 6,
        '-ms-grid-column': 1 / 2,
      },
      [theme.breakpoints.down('sm')]: {
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          '-ms-grid-row': 6,
          '-ms-grid-column': 1,
        },
      },
    },
    spagetty: {
      gridArea: 'spagetty',

      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 8,
        '-ms-grid-column': 1 / 4,
      },
      [theme.breakpoints.down('sm')]: {
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          '-ms-grid-row': 10,
          '-ms-grid-column': 1,
        },
      },
    },
    variants: {
      gridArea: 'variants',
    },
    replacements: {
      gridArea: 'replacements',
    },
    otherPrices: {
      gridArea: 'otherPrices',
    },
  }),
  themeSettings,
);

const ProductDetailPageLayout = React.forwardRef<HTMLDivElement, ProductDetailPageLayoutProps>(
  ({ className, classes: pClasses }, ref) => {
    const t = useTranslateWithFallback();
    const classes = useStyles({ classes: pClasses });
    const match = useRouteMatch<{ id: string }>();
    const product = useRecoilValue(productDetailQuery(match.params.id));

    const pageTitle = t(product.name, { acceptString: true });

    return (
      <Page
        ref={ref}
        className={clsx(classes.root, className)}
        pageTitle={pageTitle}
        classes={{
          content: classes.contentWrapper,
        }}
        breadcrumbs={<ProductDetailPageBreadcrumbs product={product} />}
      >
        <ProductDetailOtherPrices className={classes.otherPrices} product={product} />
        <ProductDetailImageGallery className={classes.imageGallery} product={product} />
        <ProductDetailName className={classes.name} product={product} withFavoriteToggle />
        <ProductDetailDescription className={classes.description} product={product} />
        <ProductDetailDiscounts className={classes.discounts} product={product} />
        <ProductDetailActions className={classes.actions} product={product} />
        {isProductCanceled(product.taxonomies) ? (
          <ProductDetailAlternatives
            className={classes.variants}
            sectionName={t('productDetailPage/replacements')}
            productAlternatives={product.replacements}
          />
        ) : (
          <ProductDetailVariants className={classes.variants} product={product} />
        )}
        <ProductDetailSpagetty className={classes.spagetty} product={product} />
      </Page>
    );
  },
);

ProductDetailPageLayout.displayName = 'ProductDetailPageLayout';

export default withThemeProps(themeSettings)(ProductDetailPageLayout);
